import { isNonEmpty } from '@sgme/fp';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { useProgressTimer } from 'hooks/useProgressTimer';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ExposedFormStatus } from 'state/forms/models';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';

type UseRowQuoteExpirationParams = {
  orderType: OrderType;
  rowId?: string;
  operationStatus?: ExposedFormStatus;
  endZonedDateTimeToTrade?: string;
  quoteTimeToTradeInSeconds?: number;
};

type UseRowQuoteExpirationResult = {
  progressInPercent: number;
  remainingTimeInSeconds: number;
  shouldDisplayCountdown: boolean;
};

export function useRowQuoteExpiration({
  orderType,
  rowId,
  operationStatus,
  endZonedDateTimeToTrade,
  quoteTimeToTradeInSeconds,
}: UseRowQuoteExpirationParams): UseRowQuoteExpirationResult {
  const dispatch = useDispatch();
  const hasDispatchedExpired = useRef(false);

  const countdownDurationInMillis = (quoteTimeToTradeInSeconds ?? 0) * 1000;
  const countdownEndTimeInMillis = parseCountdownEndTimeInMillis(endZonedDateTimeToTrade);

  const hasTradeWindow = countdownDurationInMillis > 0 && countdownEndTimeInMillis > 0;
  const isQuotedWithCountdownWindow = operationStatus === 'Quoted' && hasTradeWindow && isNonEmpty(rowId);

  const countdownStartTimeInMillis = isQuotedWithCountdownWindow
    ? countdownEndTimeInMillis - countdownDurationInMillis
    : 0;

  const { remainingTimeInSeconds, progressInPercent } = useProgressTimer(
    countdownStartTimeInMillis,
    countdownDurationInMillis,
  );

  useEffect(() => {
    if (!isQuotedWithCountdownWindow) {
      hasDispatchedExpired.current = false;
      return;
    }

    hasDispatchedExpired.current = false;
  }, [countdownEndTimeInMillis, countdownDurationInMillis, isQuotedWithCountdownWindow]);

  useEffect(() => {
    if (!isQuotedWithCountdownWindow || !rowId || countdownStartTimeInMillis === 0) {
      return;
    }

    if (hasDispatchedExpired.current) {
      return;
    }

    const timeUntilExpirationInMillis = countdownEndTimeInMillis - Date.now();

    if (timeUntilExpirationInMillis <= 0) {
      hasDispatchedExpired.current = true;
      dispatch(secondaryMarketBatchActions.rowQuoteExpired(orderType, rowId));
      return;
    }

    const expirationTimeoutId = setTimeout(() => {
      if (hasDispatchedExpired.current) {
        return;
      }

      hasDispatchedExpired.current = true;
      dispatch(secondaryMarketBatchActions.rowQuoteExpired(orderType, rowId));
    }, timeUntilExpirationInMillis);

    return () => clearTimeout(expirationTimeoutId);
  }, [countdownEndTimeInMillis, countdownStartTimeInMillis, dispatch, isQuotedWithCountdownWindow, orderType, rowId]);

  return {
    progressInPercent,
    remainingTimeInSeconds,
    shouldDisplayCountdown: isQuotedWithCountdownWindow,
  };
}

function parseCountdownEndTimeInMillis(endZonedDateTimeToTrade?: string): number {
  if (!endZonedDateTimeToTrade) {
    return 0;
  }

  const zonedDateTimeWithoutAnnotations = endZonedDateTimeToTrade.replace(/\[[^\]]+\]$/u, '');
  const parsedDate = Date.parse(zonedDateTimeWithoutAnnotations);

  return Number.isNaN(parsedDate) ? 0 : parsedDate;
}
